export interface IdeaSource {
  label: string;
  url: string;
}

export interface Idea {
  id: string;
  slug: string;
  title: string;
  anchor_subject: string;
  primary_category: string;
  secondary_categories: string[];
  editorial_type: 'concept' | 'mistake' | 'trade-off' | 'story' | 'principle' | 'tool';
  difficulty: 'foundational' | 'intermediate' | 'advanced';
  audiences: string[];
  reader_hook: string;
  illustration_url: string;
  illustration_alt: string;
  key_takeaway: string;
  body_html: string;
  nuance?: string;
  so_what?: string;
  related_idea_ids: string[];
  tags: string[];
  sources: IdeaSource[];
  published_at: string;
  updated_at: string;
  featured: boolean;
  start_here_order?: number;
  status: 'draft' | 'review' | 'approved' | 'scheduled' | 'published' | 'archived';
}

export interface Category {
  id: string;
  slug: string;
  name: string;
  editorial_intro: string;
  description: string;
  idea_count: number;
  accent_color: string;
}

/**
 * The 33 hand-written ideas this file used to hold have been replaced by the
 * 837-sketch production register. The Idea and Category shapes are kept so the
 * existing pages keep working; both are now projections of the sketch data in
 * lib/sketches, which is the source of truth.
 */
import { SKETCHES, CATEGORIES as SKETCH_CATEGORIES, START_HERE_SLUGS, startHere, type Sketch } from '@/lib/sketches';

const startIndex = (slug: string) => {
  const i = (START_HERE_SLUGS as readonly string[]).indexOf(slug);
  return i === -1 ? undefined : i + 1;
};

const toIdea = (s: Sketch, i: number): Idea => ({
  id: s.id,
  slug: s.slug,
  title: s.title,
  anchor_subject: s.title,
  primary_category: s.categorySlug,
  secondary_categories: [],
  editorial_type: 'concept',
  difficulty: 'foundational',
  audiences: [],
  reader_hook: s.headline,
  illustration_url: s.image,
  illustration_alt: s.alt,
  key_takeaway: s.takeaway,
  body_html: `<p>${s.proposition}</p>`,
  related_idea_ids: [],
  tags: [s.collection],
  sources: s.source ? [{ label: 'Primary source', url: s.source }] : [],
  published_at: '2026-09-25',
  updated_at: '2026-09-25',
  featured: i === 0,
  start_here_order: startIndex(s.slug),
  status: 'published',
});

export const IDEAS: Idea[] = SKETCHES.map(toIdea);

export const CATEGORIES: Category[] = SKETCH_CATEGORIES.map((c) => ({
  id: c.slug,
  slug: c.slug,
  name: c.name,
  editorial_intro: c.intro,
  description: c.intro,
  idea_count: c.count,
  accent_color: 'var(--accent)',
}));

export function getPublishedIdeas(): Idea[] {
  return IDEAS;
}

export function getFeaturedIdea(): Idea | undefined {
  return IDEAS[0];
}

export function getIdeaBySlug(slug: string): Idea | undefined {
  return IDEAS.find((i) => i.slug === slug);
}

export function getRelatedIdeas(idea: Idea): Idea[] {
  return IDEAS.filter((i) => i.slug !== idea.slug && i.primary_category === idea.primary_category).slice(0, 6);
}

export function getIdeasByCategory(categorySlug: string): Idea[] {
  return IDEAS.filter((i) => i.primary_category === categorySlug);
}

export function getCategoryBySlug(slug: string): Category | undefined {
  return CATEGORIES.find((c) => c.slug === slug);
}

export function getStartHereIdeas(): Idea[] {
  return startHere().map((s, i) => toIdea(s, i));
}

export function searchIdeas(query: string): Idea[] {
  const q = query.trim().toLowerCase();
  if (!q) return IDEAS;
  return IDEAS.filter((i) =>
  `${i.title} ${i.reader_hook} ${i.key_takeaway} ${i.tags.join(' ')}`.toLowerCase().includes(q));
}
